import React, { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown, GripVertical } from "lucide-react";

/**
 * A collapsible + reorderable sidebar container. Users can:
 *   • Collapse/expand each panel
 *   • Move panels up/down (persisted per-key in localStorage)
 * `panels` = [{ id, title, node, defaultOpen?, sticky? }]
 * `storageKey` should be unique per page (e.g. `channel-${username}`).
 */
export default function ReorderableSidebar({ panels, storageKey }) {
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`sb-order:${storageKey}`) || "null");
      if (Array.isArray(saved) && saved.length) return saved;
    } catch { /* ignore */ }
    return panels.map((p) => p.id);
  });

  useEffect(() => {
    // Sync in new panels appearing / removed
    const known = new Set(panels.map((p) => p.id));
    const stripped = order.filter((id) => known.has(id));
    const missing = panels.map((p) => p.id).filter((id) => !stripped.includes(id));
    const next = [...stripped, ...missing];
    if (next.join("|") !== order.join("|")) setOrder(next);
  }, [panels]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback((next) => {
    setOrder(next);
    try { localStorage.setItem(`sb-order:${storageKey}`, JSON.stringify(next)); } catch { /* ignore */ }
  }, [storageKey]);

  const move = (id, dir) => {
    const i = order.indexOf(id);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    persist(next);
  };

  const byId = Object.fromEntries(panels.map((p) => [p.id, p]));
  return (
    <div className="space-y-3">
      {order.map((id, idx) => {
        const p = byId[id];
        if (!p) return null;
        return (
          <SidePanel
            key={id}
            title={p.title}
            defaultOpen={p.defaultOpen}
            canMoveUp={idx > 0}
            canMoveDown={idx < order.length - 1}
            onUp={() => move(id, -1)}
            onDown={() => move(id, +1)}
            id={id}
          >
            {p.node}
          </SidePanel>
        );
      })}
    </div>
  );
}

function SidePanel({ id, title, defaultOpen = true, canMoveUp, canMoveDown, onUp, onDown, children }) {
  const key = `sb-open:${id}`;
  const [open, setOpen] = useState(() => {
    const v = localStorage.getItem(key);
    return v === null ? defaultOpen : v === "1";
  });
  useEffect(() => { localStorage.setItem(key, open ? "1" : "0"); }, [key, open]);

  return (
    <section className="border border-white/10 rounded-md bg-[#0F0F13] overflow-hidden" data-testid={`side-panel-${id}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 bg-white/[0.02]">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left flex-1" data-testid={`panel-toggle-${id}`}>
          <GripVertical className="w-3 h-3 text-zinc-500" />
          <span className="sc-label text-[10px] text-white">{title}</span>
          {open ? <ChevronUp className="w-3 h-3 text-zinc-500 ml-auto" /> : <ChevronDown className="w-3 h-3 text-zinc-500 ml-auto" />}
        </button>
        <div className="flex items-center gap-1 pl-2">
          <button
            onClick={onUp}
            disabled={!canMoveUp}
            className="p-1 rounded text-zinc-500 hover:text-[color:var(--sc-volt)] disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
            data-testid={`panel-up-${id}`}
          >
            <ArrowUp className="w-3 h-3" />
          </button>
          <button
            onClick={onDown}
            disabled={!canMoveDown}
            className="p-1 rounded text-zinc-500 hover:text-[color:var(--sc-volt)] disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
            data-testid={`panel-down-${id}`}
          >
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
      </div>
      {open && <div className="max-h-[80vh] overflow-y-auto">{children}</div>}
    </section>
  );
}
